<template>
  <b-sidebar
    id="edit-sidebar"
    :visible="isEditSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-edit-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{ $t("actions.edit") + " " + $tc("camera.title") }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- name -->
          <validation-provider name="name">
            <b-form-group
              class="text-capitalize"
              :label="$t('camera.name')"
              label-for="name"
            >
              <b-form-input id="name" disabled v-model="itemData.name" trim />
            </b-form-group>
          </validation-provider>

          <!-- Unit -->
          <validation-provider #default="validationContext" name="Unit">
            <b-form-group
              class="text-capitalize"
              :label="$tc('unit.title', 2)"
              label-for="id_unit"
              :state="getValidationState(validationContext)"
            >
              <select-paginate
                v-model="itemData.id_unit"
                :items="unitOptions"
                input-id="id_unit"
                :multiple="true"
                @change="updateDeviceTags"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Center -->
          <validation-provider #default="validationContext" name="Center">
            <b-form-group
              class="text-capitalize"
              :label="$tc('centers.title')"
              label-for="id_center"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_center"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="centerOptions"
                :reduce="(val) => val.value"
                input-id="id_center"
                @input="changeCenter"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Sector -->
          <validation-provider #default="validationContext" name="Sector">
            <b-form-group
              :label="$tc('sectors.title')"
              label-for="id_sector"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_sector"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sectorOptions"
                :reduce="(val) => val.value"
                input-id="id_sector"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- delayed_record_minutes -->
          <validation-provider
            #default="validationContext"
            name="delayed_record_minutes"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('camera.previous_time_of_prealarma')"
              label-for="delayed_record_minutes"
            >
              <b-form-timepicker
                id="delayed_record_minutes"
                v-model="itemData.delayed_record_minutes"
                :state="getValidationState(validationContext)"
                trim
                placeholder=""
                reset-button
              />
            </b-form-group>
          </validation-provider>

          <!-- forward_record_minutes -->
          <validation-provider
            #default="validationContext"
            name="forward_record_minutes"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('camera.later_time_of_prealarma')"
              label-for="forward_record_minutes"
            >
              <b-form-timepicker
                id="forward_record_minutes"
                v-model="itemData.forward_record_minutes"
                :state="getValidationState(validationContext)"
                trim
                placeholder=""
                reset-button
              />
            </b-form-group>
          </validation-provider>

          <!-- Tags -->
          <validation-provider #default="validationContext" name="Tag">
            <b-form-group
              class="text-capitalize"
              label="Tag"
              label-for="id_device_tag"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_device_tag"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="deviceTagsOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="id_device_tag"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- bridge -->
          <validation-provider name="bridge">
            <b-form-group label="Bridge" label-for="bridge">
              <b-form-input id="bridge" disabled v-model="itemData.bridge" trim />
            </b-form-group>
          </validation-provider>

          <!-- address -->
          <validation-provider name="address">
            <b-form-group
              class="text-capitalize"
              :label="$t('reseller.address')"
              label-for="address"
            >
              <b-form-input id="address" disabled v-model="itemData.address" trim />
            </b-form-group>
          </validation-provider>

          <!-- esn -->
          <validation-provider name="esn">
            <b-form-group label="ESN" label-for="esn">
              <b-form-input id="esn" disabled v-model="itemData.esn" trim />
            </b-form-group>
          </validation-provider>

          <validation-provider name="rtsp_available" rules="required" vid="rtsp_available">
            <b-form-checkbox
              id="rtsp_available"
              v-model="itemData.rtsp_available"
              name="rtsp_available"
              value="1"
              class="my-1 text-capitalize"
              unchecked-value="0"
              switch
            >
              {{ $t("camera_proxy.rtsp_available") }}
            </b-form-checkbox>
          </validation-provider>

          <validation-provider name="send_to_seproban" rules="required" vid="send_to_seproban">
            <b-form-checkbox
              id="send_to_seproban"
              v-model="itemData.send_to_seproban"
              name="send_to_seproban"
              value="1"
              class="my-1 text-capitalize"
              unchecked-value="0"
              switch
            >
              {{ $t("camera_proxy.send_to_seproban") }}
            </b-form-checkbox>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BFormTimepicker,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { computed, onMounted, ref } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import SelectPaginate from "../../components/SelectPaginate.vue";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormTimepicker,
    vSelect,
    BFormCheckbox,
    SelectPaginate,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isEditSidebarActive",
    event: "update:is-edit-sidebar-active",
  },
  props: {
    isEditSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: true,
    },
    centerOptions: {
      type: Array,
      required: true,
    },
    sectorOptions: {
      type: Array,
      required: true,
    },
    unitOptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const blankData = {
      id_unit: null,
      id_center: null,
      id_sector: null,
      start_time_event: "",
      end_time_event: "",
      delayed_record_minutes: "",
      forward_record_minutes: "",
      id_device_tag: null,
      rtsp_available: false,
      send_to_seproban: false,
    };
    const toast = useToast();
    const deviceTagsOptions = computed(() => {
      return store.getters["tenant/getDeviceTags"].map((item) => ({
        label: item.name,
        value: item.id,
      }));
    });
    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      store.dispatch("sector/getAll");
    };
    const onSubmit = () => {
      const req = {
        id: itemData.value.id,
        params: {
          id_unit:
            itemData.value.id_unit.length > 0
              ? itemData.value.id_unit.map((unit) => unit.value)
              : null,
          id_center: itemData.value.id_center || null,
          id_sector: itemData.value.id_sector || null,
          delayed_record_minutes: itemData.value.delayed_record_minutes,
          forward_record_minutes: itemData.value.forward_record_minutes,
          id_device_tag: itemData.value.id_device_tag,
          rtsp_available: itemData.value.rtsp_available,
          send_to_seproban: itemData.value.send_to_seproban
        },
      };
      store
        .dispatch("bd_camera/edit", req)
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-edit-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };
    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);
    const changeCenter = () => {
      store
        .dispatch("sector/getAll", {
          id_center: [itemData.value.id_center],
        })
        .then(() => {
          const sector = props.sectorOptions.filter(
            (item) => item.value == itemData.value.id_sector
          );
          if (sector.length == 0) itemData.value.id_sector = "";
        });
    };
    const updateDeviceTags = () => {
      if (itemData.value.id_unit) {
        console.log(itemData)
        const idTenants = itemData.value.id_unit.map((unit) => unit.id_tenant);
        store.dispatch("tenant/getDeviceTagsByTenantIds", { id_tenants: idTenants });
      }
    };
    const loadData = () => {
      itemData.value = props.itemEdit;
      itemData.value.bridge = props.itemEdit.bridge?.device_name;
      itemData.value.id_unit = props.itemEdit.units.map((value) => ({
        value: value.id,
        label: value.description,
        id_tenant: value.router.id_tenant,
      }));
      updateDeviceTags();
      changeCenter();
    };

    return {
      itemData,
      onSubmit,
      changeCenter,
      refFormObserver,
      getValidationState,
      loadData,
      updateDeviceTags,
      deviceTagsOptions,
      resetForm,
    };
  },
};
</script>
